import { Chess, SQUARES, type Color, type Move, type PieceSymbol, type Square } from "chess.js";
import {
  classifyGamePhase,
  type PrincipleAnchor,
  type PrincipleArrow,
  type PrincipleId,
  type PrinciplePhase,
  type PrincipleSignal,
} from "@chessla/shared";
import type { AnalyzedMove } from "./types";

export interface PrincipleCandidate {
  principle: PrincipleId;
  anchors: PrincipleAnchor[];
  arrows: PrincipleArrow[];
  metrics: Record<string, number | string | boolean>;
}

interface PrincipleHistoryContext {
  verified: boolean;
  previousMove: AnalyzedMove | null;
  priorMoves: ReadonlyMap<Square, number>;
}

const VALUES: Record<PieceSymbol, number> = { p: 100, n: 320, b: 330, r: 500, q: 900, k: 0 };
const CENTER = new Set<Square>(["d4", "e4", "d5", "e5"]);
const HOME_MINOR = new Set(["b1", "c1", "f1", "g1", "b8", "c8", "f8", "g8"]);
const fen = (value: string) => (value === "start" ? new Chess().fen() : value);
const opposite = (color: Color): Color => (color === "w" ? "b" : "w");
const rank = (square: Square) => Number(square[1]);
const file = (square: Square) => square.charCodeAt(0) - 97;
const anchor = (square: Square, role: PrincipleAnchor["role"] = "subject"): PrincipleAnchor => ({ square, role });
const arrow = (move: Move, role: PrincipleArrow["role"] = "played"): PrincipleArrow => ({
  from: move.from,
  to: move.to,
  role,
});
const pieces = (board: Chess, color: Color) =>
  SQUARES.flatMap((square) => {
    const piece = board.get(square);
    return piece?.color === color ? [{ square, type: piece.type }] : [];
  });
const pawnAttacks = (from: Square, color: Color): Square[] => {
  const nextRank = rank(from) + (color === "w" ? 1 : -1);
  return [-1, 1].flatMap((delta) => {
    const nextFile = file(from) + delta;
    return nextFile >= 0 && nextFile < 8 && nextRank >= 1 && nextRank <= 8
      ? [`${String.fromCharCode(97 + nextFile)}${nextRank}` as Square]
      : [];
  });
};
const pawnDefends = (board: Chess, square: Square, color: Color) =>
  pieces(board, color).some((piece) => piece.type === "p" && pawnAttacks(piece.square, color).includes(square));
const enemyPawnAttacks = (board: Chess, square: Square, color: Color) =>
  pieces(board, opposite(color)).some(
    (piece) => piece.type === "p" && pawnAttacks(piece.square, opposite(color)).includes(square),
  );
const hanging = (board: Chess, color: Color) =>
  pieces(board, color).filter(
    (piece) =>
      piece.type !== "k" && board.isAttacked(piece.square, opposite(color)) && !board.isAttacked(piece.square, color),
  );
const withTurn = (position: string, color: Color) => {
  const fields = position.split(" ");
  fields[1] = color;
  fields[3] = "-";
  return new Chess(fields.join(" "));
};
const mobility = (board: Chess, square: Square, color: Color) =>
  withTurn(board.fen(), color).moves({ square, verbose: true }).length;
const material = (board: Chess, color: Color) =>
  pieces(board, color).reduce((sum, piece) => sum + VALUES[piece.type], 0);
const connectedRooks = (board: Chess, color: Color) => {
  const rooks = pieces(board, color).filter((piece) => piece.type === "r");
  return rooks.some((left, index) =>
    rooks.slice(index + 1).some((right) => {
      if (rank(left.square) !== rank(right.square)) return false;
      const [from, to] = [file(left.square), file(right.square)].sort((a, b) => a - b);
      return Array.from(
        { length: to - from - 1 },
        (_, offset) => `${String.fromCharCode(98 + from + offset)}${rank(left.square)}` as Square,
      ).every((square) => !board.get(square));
    }),
  );
};
const pawnWeaknesses = (board: Chess, color: Color) => {
  const pawns = pieces(board, color).filter((piece) => piece.type === "p");
  const counts = Array.from({ length: 8 }, (_, index) => pawns.filter((pawn) => file(pawn.square) === index).length);
  return counts.reduce(
    (sum, count, index) =>
      sum +
      Math.max(0, count - 1) +
      (count > 0 && (counts[index - 1] ?? 0) === 0 && (counts[index + 1] ?? 0) === 0 ? count : 0),
    0,
  );
};
const passedPawns = (board: Chess, color: Color) =>
  pieces(board, color).filter(
    (piece) =>
      piece.type === "p" &&
      !pieces(board, opposite(color)).some(
        (enemy) =>
          enemy.type === "p" &&
          Math.abs(file(enemy.square) - file(piece.square)) <= 1 &&
          (color === "w" ? rank(enemy.square) > rank(piece.square) : rank(enemy.square) < rank(piece.square)),
      ),
  );
const centerDistance = (square: Square) =>
  Math.min(
    ...["d4", "e4", "d5", "e5"].map(
      (target) => Math.abs(file(square) - file(target as Square)) + Math.abs(rank(square) - rank(target as Square)),
    ),
  );
const opposition = (board: Chess, color: Color) => {
  const own = pieces(board, color).find((piece) => piece.type === "k")?.square;
  const enemy = pieces(board, opposite(color)).find((piece) => piece.type === "k")?.square;
  return own && enemy
    ? (file(own) === file(enemy) && Math.abs(rank(own) - rank(enemy)) === 2) ||
        (rank(own) === rank(enemy) && Math.abs(file(own) - file(enemy)) === 2)
    : false;
};

export const principleCandidatesForMove = (
  position: string,
  moveSan: string,
  history?: PrincipleHistoryContext,
): PrincipleCandidate[] => {
  const before = new Chess(fen(position));
  const color = before.turn();
  const move = before.move(moveSan);
  if (!move) return [];
  const after = before;
  const original = new Chess(fen(position));
  const piece = original.get(move.from);
  if (!piece) return [];
  const currentPhase = classifyGamePhase(original.fen());
  const candidates: PrincipleCandidate[] = [];
  const add = (
    principle: PrincipleId,
    anchors: PrincipleAnchor[] = [anchor(move.to)],
    metrics: Record<string, number | string | boolean> = {},
    arrows: PrincipleArrow[] = [arrow(move)],
  ) => candidates.push({ principle, anchors, arrows, metrics });
  const developedMinor =
    currentPhase === "opening" &&
    (piece.type === "n" || piece.type === "b") &&
    HOME_MINOR.has(move.from) &&
    !HOME_MINOR.has(move.to);
  const priorPieceMoves = history?.verified ? history.priorMoves.get(move.from) : undefined;
  const freshDevelopment = developedMinor && (!history || priorPieceMoves === 0);

  if (currentPhase === "opening" && CENTER.has(move.to)) add("center_claim", [anchor(move.to, "key_square")]);
  if (freshDevelopment) add("pieces_out", [anchor(move.to)]);
  if (freshDevelopment)
    add(
      "tempo_discipline",
      [anchor(move.from, "subject"), anchor(move.to, "target")],
      history ? { playedPiecePriorMoves: priorPieceMoves ?? -1, historyVerified: history.verified } : {},
    );
  const queenHome = color === "w" ? "d1" : "d8";
  const queenPriorMoves = history?.verified ? history.priorMoves.get(queenHome) : undefined;
  if (freshDevelopment && original.get(queenHome)?.type === "q" && (!history || queenPriorMoves === 0))
    add(
      "queen_patience",
      [anchor(queenHome, "support"), anchor(move.to)],
      history ? { queenPriorMoves: queenPriorMoves ?? -1, historyVerified: history.verified } : {},
    );
  if (
    currentPhase === "opening" &&
    piece.type === "p" &&
    (file(move.from) === 3 || file(move.from) === 4) &&
    (CENTER.has(move.to) || CENTER.has(move.from))
  )
    add("pawn_economy", [anchor(move.to, "key_square")]);
  if (move.isKingsideCastle() || move.isQueensideCastle())
    add("king_shelter", [
      anchor(move.to),
      anchor(
        color === "w" ? (move.isKingsideCastle() ? "f1" : "d1") : move.isKingsideCastle() ? "f8" : "d8",
        "support",
      ),
    ]);
  if (
    piece.type === "p" &&
    CENTER.has(move.to) &&
    pieces(after, opposite(color)).some(
      (enemy) =>
        enemy.type === "p" &&
        pawnAttacks(move.to, color).includes(enemy.square) &&
        pawnAttacks(enemy.square, opposite(color)).includes(move.to),
    )
  )
    add("center_lock", [
      anchor(move.to),
      ...pieces(after, opposite(color))
        .filter((enemy) => enemy.type === "p" && pawnAttacks(move.to, color).includes(enemy.square))
        .slice(0, 1)
        .map((enemy) => anchor(enemy.square, "target")),
    ]);
  if (!connectedRooks(original, color) && connectedRooks(after, color))
    add(
      "rook_harmony",
      pieces(after, color)
        .filter((entry) => entry.type === "r")
        .map((entry) => anchor(entry.square, "support")),
    );
  const looseBefore = hanging(original, color);
  const looseAfter = hanging(after, color);
  const priorLoose =
    history?.verified && history.previousMove && !history.previousMove.isPlayerMove
      ? hanging(new Chess(fen(history.previousMove.fenBefore)), color)
      : null;
  const opponentCreatedThreat = priorLoose
    ? looseBefore.some(
        (entry) => entry.square === move.from && !priorLoose.some((prior) => prior.square === entry.square),
      )
    : null;
  if (
    looseBefore.some((entry) => entry.square === move.from) &&
    looseAfter.length < looseBefore.length &&
    (!history || !history.verified || opponentCreatedThreat === true)
  )
    add(
      "threat_read",
      [anchor(move.from, "danger"), anchor(move.to)],
      history ? { opponentCreatedThreat: opponentCreatedThreat === true, historyVerified: history.verified } : {},
    );
  if (move.isCapture() || move.san.includes("+"))
    add("force_first", [anchor(move.to, move.isCapture() ? "target" : "key_square")]);
  if (looseAfter.length < looseBefore.length) add("nothing_loose", [anchor(move.from, "danger"), anchor(move.to)]);
  if (piece.type !== "p" && piece.type !== "k") {
    const ownMobility = pieces(original, color)
      .filter((entry) => entry.type !== "p" && entry.type !== "k")
      .map((entry) => mobility(original, entry.square, color));
    const beforeMobility = mobility(original, move.from, color);
    const afterMobility = mobility(after, move.to, color);
    if (ownMobility.length && beforeMobility === Math.min(...ownMobility) && afterMobility > beforeMobility)
      add("worst_piece", [anchor(move.from), anchor(move.to, "target")], { beforeMobility, afterMobility });
  }
  if (
    (piece.type === "r" || piece.type === "q") &&
    !pieces(after, "w")
      .concat(pieces(after, "b"))
      .some((entry) => entry.type === "p" && file(entry.square) === file(move.to))
  )
    add("file_control", [anchor(move.to, "key_square")], { file: move.to[0] });
  if (
    piece.type === "n" &&
    (color === "w" ? rank(move.to) >= 5 : rank(move.to) <= 4) &&
    pawnDefends(after, move.to, color) &&
    !enemyPawnAttacks(after, move.to, color)
  )
    add("outpost", [anchor(move.to, "key_square")]);
  if (
    piece.type === "p" &&
    !move.isCapture() &&
    pawnAttacks(move.to, color).some(
      (square) => after.get(square)?.color === opposite(color) && after.get(square)?.type === "p",
    ) &&
    pawnDefends(after, move.to, color)
  )
    add("break_timing", [
      anchor(move.to),
      ...pawnAttacks(move.to, color)
        .filter((square) => after.get(square)?.color === opposite(color))
        .map((square) => anchor(square, "target")),
    ]);
  const weaknessBefore = pawnWeaknesses(original, color);
  const weaknessAfter = pawnWeaknesses(after, color);
  if (piece.type === "p" && weaknessAfter < weaknessBefore)
    add("pawn_integrity", [anchor(move.from, "danger"), anchor(move.to, "support")], { weaknessBefore, weaknessAfter });
  if (
    move.isCapture() &&
    move.captured &&
    move.captured !== "p" &&
    material(original, color) - material(original, opposite(color)) >= 300
  )
    add("clean_conversion", [anchor(move.to, "target")], {
      materialLeadCp: material(original, color) - material(original, opposite(color)),
    });
  if (piece.type === "k" && currentPhase === "endgame" && centerDistance(move.to) < centerDistance(move.from))
    add("active_king", [anchor(move.from), anchor(move.to, "key_square")]);
  if (piece.type === "k" && currentPhase === "endgame" && opposition(after, color))
    add("opposition", [
      anchor(move.to, "key_square"),
      ...pieces(after, opposite(color))
        .filter((entry) => entry.type === "k")
        .map((entry) => anchor(entry.square, "target")),
    ]);
  const passers = passedPawns(after, color);
  if (passers.some((pawn) => after.isAttacked(pawn.square, color) && !original.isAttacked(pawn.square, color)))
    add("passer_support", [
      anchor(move.to, "support"),
      ...passers.slice(0, 1).map((pawn) => anchor(pawn.square, "subject")),
    ]);
  return candidates;
};

const initialPieceHistory = (board: Chess) =>
  new Map<Square, number>(SQUARES.flatMap((square) => (board.get(square) ? [[square, 0] as const] : [])));

const principleHistory = (moves: readonly AnalyzedMove[]): PrincipleHistoryContext[] => {
  if (!moves.length) return [];
  try {
    let board = new Chess(fen(moves[0].fenBefore));
    let priorMoves = initialPieceHistory(board);
    let verified = board.fen() === new Chess().fen();
    let previousMove: AnalyzedMove | null = null;
    return moves.map((analyzed) => {
      if (board.fen() !== fen(analyzed.fenBefore)) {
        board = new Chess(fen(analyzed.fenBefore));
        priorMoves = initialPieceHistory(board);
        verified = false;
        previousMove = null;
      }
      const context: PrincipleHistoryContext = { verified, previousMove, priorMoves: new Map(priorMoves) };
      let played: Move | null = null;
      try {
        played = board.move(analyzed.san);
      } catch {
        played = null;
      }
      if (!played || board.fen() !== analyzed.fenAfter) {
        verified = false;
        previousMove = null;
        return context;
      }
      const count = priorMoves.get(played.from) ?? 0;
      priorMoves.delete(played.from);
      if (played.isEnPassant()) priorMoves.delete(`${played.to[0]}${played.from[1]}` as Square);
      else if (played.isCapture()) priorMoves.delete(played.to);
      priorMoves.set(played.to, count + 1);
      if (played.isKingsideCastle() || played.isQueensideCastle()) {
        const homeRank = played.color === "w" ? "1" : "8";
        const rookFrom = `${played.isKingsideCastle() ? "h" : "a"}${homeRank}` as Square;
        const rookTo = `${played.isKingsideCastle() ? "f" : "d"}${homeRank}` as Square;
        const rookCount = priorMoves.get(rookFrom) ?? 0;
        priorMoves.delete(rookFrom);
        priorMoves.set(rookTo, rookCount + 1);
      }
      previousMove = analyzed;
      return context;
    });
  } catch {
    return moves.map(() => ({ verified: false, previousMove: null, priorMoves: new Map<Square, number>() }));
  }
};

const legalProof = (position: string, sans: string[]) => {
  const board = new Chess(fen(position));
  const proof: string[] = [];
  for (const san of sans.slice(0, 8)) {
    let move: Move;
    try {
      move = board.move(san);
    } catch {
      return null;
    }
    if (!move) return null;
    proof.push(move.san);
  }
  return proof;
};

const toSignal = (
  candidate: PrincipleCandidate,
  status: "applied" | "missed",
  signalPhase: PrinciplePhase,
  alternativeSan: string | null,
  proofSans: string[],
  evaluationImpactCp: number | null,
): PrincipleSignal => ({
  principle: candidate.principle,
  status,
  phase: signalPhase,
  detectorVersion: 1,
  anchors: candidate.anchors,
  arrows: candidate.arrows.map((item) =>
    status === "missed" && item.role === "played" ? { ...item, role: "verified_alternative" } : item,
  ),
  alternativeSan,
  proofSans,
  evaluationImpactCp,
  metrics: candidate.metrics,
});

export const principleSignalsForVerifiedMove = (
  position: string,
  playedSan: string,
  bestMoveSan: string | null,
  centipawnLoss: number,
): PrincipleSignal[] => {
  let before: Chess;
  let played: Move;
  try {
    before = new Chess(fen(position));
    if (before.inCheck() || before.moves().length <= 1) return [];
    played = before.move(playedSan)!;
    if (!played) return [];
  } catch {
    return [];
  }

  const signalPhase = classifyGamePhase(fen(position));
  const playedCandidates = principleCandidatesForMove(position, played.san);
  const signals =
    centipawnLoss <= 30
      ? playedCandidates.map((candidate) => toSignal(candidate, "applied", signalPhase, null, [played.san], null))
      : [];
  if (
    centipawnLoss < 50 ||
    !bestMoveSan ||
    bestMoveSan === played.san ||
    played.isCapture() ||
    played.isPromotion() ||
    played.isKingsideCastle() ||
    played.isQueensideCastle() ||
    played.san.includes("+")
  )
    return signals;

  const proof = legalProof(position, [bestMoveSan]);
  if (!proof) return signals;
  const playedIds = new Set(playedCandidates.map(({ principle }) => principle));
  for (const candidate of principleCandidatesForMove(position, bestMoveSan)) {
    if (!playedIds.has(candidate.principle)) {
      signals.push(toSignal(candidate, "missed", signalPhase, proof[0], proof, Math.round(centipawnLoss)));
    }
  }
  return signals;
};

export const buildPrincipleSignals = <T extends AnalyzedMove>(moves: T[]): T[] => {
  const history = principleHistory(moves);
  return moves.map((move, index) => {
    const empty = { ...move, principleSignals: [] as PrincipleSignal[] };
    if (move.motifs.some((motif) => motif.toLowerCase().includes("book"))) return empty;
    let before: Chess;
    let played: Move;
    try {
      before = new Chess(fen(move.fenBefore));
      if (before.inCheck() || before.moves().length <= 1) return empty;
      const replay = new Chess(before.fen());
      played = replay.move(move.san)!;
      if (!played || replay.fen() !== move.fenAfter) return empty;
    } catch {
      return empty;
    }
    const playedCandidates = principleCandidatesForMove(move.fenBefore, move.san, history[index]);
    const signalPhase = classifyGamePhase(before.fen());
    const signals =
      move.centipawnLoss <= 30 && !move.isImprecision && !move.isMistake && !move.isBlunder
        ? playedCandidates.map((candidate) => toSignal(candidate, "applied", signalPhase, null, [played.san], null))
        : [];
    if (
      move.centipawnLoss < 50 ||
      !move.bestMoveSan ||
      move.bestMoveSan === move.san ||
      played.isCapture() ||
      played.isPromotion() ||
      played.isKingsideCastle() ||
      played.isQueensideCastle() ||
      played.san.includes("+")
    )
      return { ...move, principleSignals: signals };
    const proof = legalProof(move.fenBefore, [move.bestMoveSan, ...move.bestContinuation]);
    if (!proof) return { ...move, principleSignals: signals };
    const playedIds = new Set(playedCandidates.map((candidate) => candidate.principle));
    for (const candidate of principleCandidatesForMove(move.fenBefore, move.bestMoveSan, history[index])) {
      if (!playedIds.has(candidate.principle)) {
        const missedCandidate =
          candidate.principle === "tempo_discipline"
            ? {
                ...candidate,
                metrics: {
                  ...candidate.metrics,
                  alternativePiecePriorMoves: candidate.metrics.playedPiecePriorMoves,
                  playedPiecePriorMoves: history[index].priorMoves.get(played.from) ?? -1,
                },
              }
            : candidate;
        signals.push(toSignal(missedCandidate, "missed", signalPhase, proof[0], proof, Math.round(move.centipawnLoss)));
      }
    }
    return { ...move, principleSignals: signals };
  });
};
